import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { Kpi, AudioFX } from '../ui/primitives';
import { usePackingStation } from '../contexts/PackingStationContext';
import { useKiosk } from './StationApp';
import { usePrint } from './PrintProgress';
import { printProductsReport } from '../admin/actions';
import { API_URL } from '../config/api';
import { useStockConfig, useStockOverview, StockOverview } from '../magazyn/api';

const FEED_ICON: Record<string, [string, string, string]> = {
  packed: ['check', 'var(--success-soft)', 'var(--success-text)'],
  print: ['printer', 'var(--accent-soft)', 'var(--accent-text)'],
  scan: ['scan-line', 'var(--surface-3)', 'var(--fg-2)'],
  print_err: ['alert-circle', 'var(--danger-soft)', 'var(--danger-text)'],
  start: ['play', 'var(--accent-soft)', 'var(--accent-text)'],
};

/* Raport produktów — zakres liczony przez UCS (kontroler dat today/bird), druk przez agenta */
function ReportSheet({ onClose }: { onClose: () => void }) {
  const { push } = useKiosk();
  const { startPrint } = usePrint();
  const [range, setRange] = useState<'open' | 'new' | 'range'>('open');
  const [preview, setPreview] = useState(false);
  const opts: ['open' | 'new' | 'range', string][] = [
    ['open', 'Na teraz (otwarte zamówienia)'],
    ['new', 'Nowe od ostatniego raportu'],
    ['range', 'Przedział dat dostaw'],
  ];
  const modeByRange: Record<typeof range, 'now' | 'since' | 'range'> = { open: 'now', new: 'since', range: 'range' };
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  function print() {
    if (range === 'range' && !dateFrom) { push('Podaj datę „od"', 'err'); return; }
    const mode = modeByRange[range];
    const from = range === 'range' && dateFrom ? dateFrom : undefined;
    const to = range === 'range' && dateTo ? dateTo : undefined;
    onClose();
    startPrint({
      title: preview ? 'Raport produktów (poglądowy)' : 'Raport produktów',
      subtitle: opts.find(([id]) => id === range)?.[1],
      unit: 'page',
      preparing: 'Liczenie raportu i przygotowanie wydruku…',
      run: async () => {
        const r = await printProductsReport({ mode, from, to, preview });
        if (r.jobId) return { jobIds: [r.jobId], detail: r.message };
        // Pusty zakres to informacja, a brak drukarki raportów — błąd do naprawienia w panelu.
        return r.empty ? { jobIds: [], info: r.message } : { jobIds: [], error: r.message };
      },
    });
  }

  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h3>Raport produktów</h3>
        <p className="sub">Zakres liczy UCS wg włączonego kontrolera dat (today/bird)</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {opts.map(([id, lbl]) => (
            <button key={id} className={`pkg-type ${range === id ? 'suggested' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => setRange(id)}>
              <Icon name={range === id ? 'check-circle-2' : 'circle'} size={18} style={{ color: range === id ? 'var(--accent)' : 'var(--fg-3)' }} />
              <span style={{ fontWeight: 600 }}>{lbl}</span>
            </button>
          ))}
          {range === 'range' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <input className="select" style={{ flex: 1, minHeight: 44 }} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <input className="select" style={{ flex: 1, minHeight: 44 }} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          )}
          <button className={`pkg-type ${preview ? 'suggested' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => setPreview((v) => !v)}>
            <Icon name={preview ? 'check-circle-2' : 'circle'} size={18} style={{ color: preview ? 'var(--accent)' : 'var(--fg-3)' }} />
            <span style={{ fontWeight: 600 }}>Wydruk poglądowy</span>
          </button>
        </div>
        <button className="big-btn bb-primary" style={{ width: '100%' }} onClick={print}>
          <Icon name="file-text" size={18} /> Drukuj raport
        </button>
      </div>
    </div>
  );
}

/* Jedna linijka pod kafelkiem Magazynu — co teraz najważniejsze. */
function stockLine(o: StockOverview | undefined): string {
  if (!o) return 'dostawy · braki · dobranie';
  if (!o.stockKnown) return 'Wpisz dostawę';
  if (o.openRun) return 'Rozlicz dobranie';
  if (!o.demandAvailable) return `${o.totals.onHand} szt. na stanie`;
  if (o.totals.toTake > 0) return `Brakuje ${o.totals.toTake} szt.`;
  return 'Wszystko na stanie';
}

/* Radio — polskie stacje przez PackingStationContext */
const RADIO_STATIONS = [
  { id: 'rmf', name: 'RMF FM', city: 'Kraków', logo: '', streamUrl: 'https://rs6-krk2.rmfstream.pl/rmf_fm' },
  { id: 'zet', name: 'Radio ZET', city: 'Warszawa', logo: '', streamUrl: 'https://zt02.cdn.eurozet.pl/zet-net.mp3' },
  { id: 'trojka', name: 'Trójka', city: 'Warszawa', logo: '', streamUrl: 'https://stream3.polskieradio.pl:8904/' },
  { id: 'eska', name: 'Radio ESKA', city: 'Warszawa', logo: '', streamUrl: 'https://waw01-01.ic.smcdn.pl/t051-1.mp3' },
  { id: 'antyradio', name: 'Antyradio', city: 'Warszawa', logo: '', streamUrl: 'https://n-09-08.dcs.redcdn.pl/sc/o2/Eurozet/live/antyradio.livx' },
  { id: 'chillizet', name: 'Chilli ZET', city: 'Warszawa', logo: '', streamUrl: 'https://zt03.cdn.eurozet.pl/chillizet-net.mp3' },
  { id: 'rmfclassic', name: 'RMF Classic', city: 'Kraków', logo: '', streamUrl: 'https://rs6-krk2.rmfstream.pl/rmf_classic' },
  { id: 'meloradio', name: 'Meloradio', city: 'Warszawa', logo: '', streamUrl: 'https://zt04.cdn.eurozet.pl/meloradio-net.mp3' },
];

function RadioSheet({ onClose }: { onClose: () => void }) {
  const { radioRef, radioStation, setRadioStation, radioPlaying, setRadioPlaying, radioVolume, setRadioVolume } = usePackingStation();

  function pick(st: typeof RADIO_STATIONS[number]) {
    AudioFX.click();
    if (radioStation?.id === st.id && radioPlaying) {
      radioRef.current?.pause();
      setRadioPlaying(false);
      return;
    }
    setRadioStation(st);
    setTimeout(() => { radioRef.current?.play().catch(() => undefined); }, 80);
  }

  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h3>Radio</h3>
        <p className="sub">Polskie stacje · gra w tle podczas pakowania</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {RADIO_STATIONS.map((st) => {
            const playing = radioStation?.id === st.id && radioPlaying;
            return (
              <button key={st.id} className={`radio-row ${playing ? 'playing' : ''}`} onClick={() => pick(st)}>
                <div className="ri"><Icon name={playing ? 'pause' : 'play'} size={18} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{st.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{st.city}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="volume-2" size={18} style={{ color: 'var(--fg-3)' }} />
          <input type="range" min={0} max={100} value={Math.round(radioVolume * 100)} style={{ flex: 1 }}
            onChange={(e) => setRadioVolume(+e.target.value / 100)} />
          <span className="mono" style={{ fontSize: 13, width: 40, textAlign: 'right' }}>{Math.round(radioVolume * 100)}%</span>
        </div>
      </div>
    </div>
  );
}

export default function DashboardScreen() {
  const navigate = useNavigate();
  const { user, station, logout, radioStation, radioPlaying } = usePackingStation();
  const { compact } = useKiosk();
  const [sheet, setSheet] = useState<null | 'report' | 'radio'>(null);
  // Magazyn (moduł opcjonalny) — kafelek tylko, gdy admin go włączył.
  const stockOn = !!useStockConfig().data?.enabled;
  const { data: stock } = useStockOverview({ enabled: stockOn, refetchInterval: 60000 });
  const stockShort = !!stock && stock.stockKnown && stock.demandAvailable && stock.totals.toTake > 0;

  const { data: stats } = useQuery<any>({
    queryKey: ['kiosk-stats'],
    queryFn: async () => {
      const [dash, pack] = await Promise.all([
        axios.get(`${API_URL}/api/orders/stats/dashboard`),
        axios.get(`${API_URL}/api/packing/orders`),
      ]);
      // /api/packing/orders zwraca { packages, categories } (nie tablicę) — bierzemy .packages.
      // „Oczekujące" = cała kolejka /packages/now (źródło prawdy). NIE odejmujemy lokalnie
      // „spakowanych": zamówienie wypada z kolejki dopiero po udanym fulfillmencie w Shopify.
      const pend = Array.isArray(pack.data?.packages) ? pack.data.packages.length : 0;
      return { ...dash.data, relevantPending: pend };
    },
    refetchInterval: 30000,
  });

  const { data: history = [] } = useQuery<any[]>({
    queryKey: ['kiosk-history'],
    queryFn: async () => (await axios.get(`${API_URL}/api/packing/history`)).data,
    refetchInterval: 30000,
  });

  function handleLogout() {
    logout();
    navigate('/packing-station/login');
  }

  // „Nieznany" z backendu = nie udało się rozwiązać operatora dla starszego wpisu.
  // Pokaż wtedy klienta zamówienia, a w ostateczności kreskę — nie surowe „Nieznany".
  const clean = (v?: string) => (v && v.trim() && v.trim() !== 'Nieznany' ? v.trim() : '');
  const feed = history.slice(0, 6).map((h: any) => ({
    kind: 'packed',
    what: h.order_name,
    who: clean(h.user_name) || clean(h.customer_name) || '—',
    st: h.station_name || '—',
    ago: h.completed_at ? new Date(h.completed_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : '',
  }));

  return (
    <div className="screen">
      <div className="k-header">
        <div style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--accent)', color: '#fff', display: 'grid', placeItems: 'center' }}><Icon name="package-check" size={22} /></div>
        <div>
          <div className="k-title">Cześć, {user?.name?.split(' ')[0] || 'Operatorze'}</div>
          <div className="k-sub">{station?.name || station?.id}{station?.location ? ` · ${station.location}` : ''}</div>
        </div>
        <div className="right">
          <span className="pill pill-ok"><Icon name="circle" size={9} /> Na żywo</span>
          <button className="icon-btn" title="Wyloguj" onClick={handleLogout}><Icon name="log-out" size={18} /></button>
        </div>
      </div>
      <div className="scrollable" style={{ flex: 1 }}>
        <div className="section-pad">
          <div className="kpi-grid" style={{ gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(4,1fr)' }}>
            <Kpi icon="clock" label="Oczekujące" value={stats?.relevantPending ?? '—'} />
            <Kpi icon="package-check" label="Spakowane dziś" value={stats?.packedToday ?? '—'} />
            <Kpi icon="timer" label="Śr. czas" value={stats?.avgPackingTime ?? '—'} />
            <Kpi icon="tag" label="Etykiety dziś" value={stats?.labelsToday ?? '—'} />
          </div>

          <div className="action-grid" style={{ marginTop: 18, gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(3,1fr)' }}>
            <button className="action-tile primary" style={{ gridColumn: compact && !stockOn ? '1 / -1' : 'auto' }} onClick={() => navigate('/packing-station/orders')}>
              <div className="ico"><Icon name="scan-line" size={26} /></div>
              <div><div className="at-title">Pakuj</div><div className="at-sub">{stats?.relevantPending ?? '—'} zamówień oczekuje</div></div>
            </button>
            {stockOn && (
              <button className="action-tile" onClick={() => navigate('/packing-station/magazyn')}>
                <div className="ico" style={stockShort ? { background: 'var(--danger-soft)', color: 'var(--danger-text)' } : undefined}><Icon name="warehouse" size={26} /></div>
                <div>
                  <div className="at-title">Magazyn</div>
                  <div className="at-sub" style={stockShort ? { color: 'var(--danger-text)', opacity: 1, fontWeight: 600 } : undefined}>{stockLine(stock)}</div>
                </div>
              </button>
            )}
            <button className="action-tile" onClick={() => navigate('/packing-station/done')}>
              <div className="ico"><Icon name="check-check" size={26} /></div>
              <div><div className="at-title">Zakończone</div><div className="at-sub">{stats?.packedToday ?? '—'} dziś</div></div>
            </button>
            <button className="action-tile" onClick={() => navigate('/packing-station/prints')}>
              <div className="ico"><Icon name="printer" size={26} /></div>
              <div><div className="at-title">Wydruki</div><div className="at-sub">etykiety logo · SHOKUPAN</div></div>
            </button>
            <button className="action-tile" onClick={() => setSheet('report')}>
              <div className="ico"><Icon name="file-bar-chart" size={26} /></div>
              <div><div className="at-title">Raport</div><div className="at-sub">produkty do spakowania</div></div>
            </button>
            <button className="action-tile" onClick={() => setSheet('radio')}>
              <div className="ico"><Icon name="radio" size={26} /></div>
              <div><div className="at-title">Radio</div><div className="at-sub">{radioPlaying && radioStation ? `${radioStation.name} · gra` : 'wybierz stację'}</div></div>
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="section-title">
              <span><Icon name="activity" size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Ostatnia aktywność</span>
              <span style={{ color: 'var(--accent-text)' }}>Na żywo</span>
            </div>
            <div className="kfeed">
              {feed.map((f, i) => {
                const [ic, bg, fg] = FEED_ICON[f.kind];
                return (
                  <div className="feed-row" key={i}>
                    <div className="ic" style={{ background: bg, color: fg }}><Icon name={ic} size={15} /></div>
                    <div className="t"><b>{f.what}</b> · {f.who}</div>
                    <div className="ago">{f.st} · {f.ago}</div>
                  </div>
                );
              })}
              {feed.length === 0 && <div className="muted" style={{ fontSize: 13, padding: '10px 12px' }}>Brak dzisiejszej aktywności.</div>}
            </div>
          </div>
        </div>
      </div>

      {sheet === 'report' && <ReportSheet onClose={() => setSheet(null)} />}
      {sheet === 'radio' && <RadioSheet onClose={() => setSheet(null)} />}
    </div>
  );
}
